import { Check, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import type { InvitePreview } from '../../lib/friends.ts';
import { acceptInvite, previewInvite } from '../../lib/friends.ts';

/* La page qui s'ouvre au bout du lien.
 *
 * Elle MONTRE avant d'agir. Ouvrir une URL n'est pas consentir : un lien reçu
 * dans une conversation se clique par curiosité, et se retrouver lié à
 * quelqu'un pour l'avoir ouvert serait un piège. Le lien n'est consommé que par
 * l'appui sur le bouton.
 */

export function FriendInvite() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [invite, setInvite] = useState<InvitePreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) return;
        previewInvite(token)
            .then(setInvite)
            .catch((e: unknown) => setError(
                e instanceof ApiError ? e.message : 'Lien illisible',
            ));
    }, [token]);

    const accept = async (): Promise<void> => {
        if (!token) return;
        setBusy(true); setError(null);
        try {
            await acceptInvite(token);
            setDone(true);
            setTimeout(() => void navigate('/amis'), 1400);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de valider ce lien');
            setBusy(false);
        }
    };

    if (error) {
        return (
            <div className="space-y-4 py-8 text-center">
                <div role="alert" className="alert alert-warning text-sm"><span>{error}</span></div>
                <p className="text-sm opacity-60">
                    Demandez à votre partenaire de vous en envoyer un nouveau : un lien
                    ne sert qu'une fois, et expire au bout de 7 jours.
                </p>
                <Link to="/amis" className="btn btn-ghost btn-sm">Mes partenaires</Link>
            </div>
        );
    }

    if (!invite) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-ring loading-lg text-primary" />
            </div>
        );
    }

    if (done) {
        return (
            <div className="space-y-3 py-16 text-center">
                <Check size={48} className="mx-auto text-success" />
                <p className="text-lg font-semibold">
                    {invite.displayName} fait maintenant partie de vos partenaires.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6 py-8">
            <div className="flex flex-col items-center gap-3 text-center">
                <Avatar name={invite.displayName} avatarUrl={invite.avatarUrl} size={80} />
                <h1 className="text-2xl font-bold tracking-tight">{invite.displayName}</h1>
                <p className="text-sm opacity-70">
                    vous propose de devenir partenaires de putting.
                </p>
            </div>

            {invite.alreadyFriends ? (
                <div className="rounded-box bg-base-200 p-4 text-center text-sm">
                    Vous êtes déjà partenaires — rien à faire.
                    <Link to="/putting" className="btn btn-primary btn-block mt-3">
                        Aller au putting
                    </Link>
                </div>
            ) : invite.isMine ? (
                <div className="rounded-box bg-base-200 p-4 text-center text-sm opacity-80">
                    Ce lien est le vôtre. Partagez-le à la personne que vous souhaitez
                    ajouter — il ne peut pas vous ajouter à vous-même.
                </div>
            ) : (
                <div className="space-y-2">
                    <button type="button" disabled={busy} onClick={() => void accept()}
                        className="btn btn-primary btn-block h-14 gap-2 text-lg font-bold tap-target">
                        <UserPlus size={20} /> Accepter
                    </button>
                    <p className="text-center text-xs opacity-60">
                        Être partenaires permet de s'inviter à des parties de putting.
                        Cela ne donne accès ni à votre matériel, ni à vos statistiques,
                        ni à vos séances.
                    </p>
                    <Link to="/" className="btn btn-ghost btn-block btn-sm">Plus tard</Link>
                </div>
            )}
        </div>
    );
}
